import { useMemo, useState } from 'react';
import { LIBELLE_LANGUE, MODELES_MAIL, TYPES_MAIL, type TypeMail } from '@mosaik/core';
import type { DocumentReglages } from '../api/types';
import { aEnregistrer, aResynchroniser, vueAttendue } from './modeles-mail';

/**
 * Les modèles de tous les mails que l'application compose.
 *
 * Ils vivaient dans quatre fichiers sous quatre formes, et trois d'entre eux
 * n'étaient modifiables qu'en déployant — sans que rien à l'écran ne dise
 * lesquels. Ici, un seul endroit : on choisit le mail, on choisit la langue,
 * on corrige.
 *
 * **Vider un champ le ramène au modèle d'origine.** C'est le repli qui vaut
 * partout ailleurs dans ces réglages, et il joue champ par champ : vider
 * l'objet n'efface pas le corps. C'est le moyen de revenir en arrière sur une
 * seule ligne sans avoir à la retrouver.
 */
export function ModelesMail({
  reglages, onEnregistrer,
}: {
  reglages: DocumentReglages;
  onEnregistrer: (champs: Record<string, unknown>, message: string) => void;
}) {
  const [type, setType] = useState<TypeMail>('commande_fournisseur');
  const description = MODELES_MAIL[type];
  const [langue, setLangue] = useState<string>(description.langues[0]!);

  // Changer de mail peut invalider la langue : tous ne parlent pas les cinq.
  const langueActive = description.langues.includes(langue) ? langue : description.langues[0]!;

  const enregistre = reglages.modelesMail?.[type]?.[langueActive];
  const defaut = description.defaut[langueActive] ?? description.defaut[description.langues[0]!]!;

  /*
   * Les zones de saisie portent le texte QUI S'APPLIQUE, dès le premier
   * affichage : celui qui est enregistré, ou le modèle d'origine à défaut.
   *
   * Elles partaient de deux chaînes vides, et ne se remplissaient qu'au
   * changement de mail ou de langue : un modèle déjà enregistré s'ouvrait donc
   * sur des champs vides, comme s'il ne l'avait jamais été. Et un modèle jamais
   * modifié ne montrait son texte qu'en `placeholder`, en gris, effacé à la
   * première frappe — corriger un mot obligeait à retaper le mail entier.
   *
   * La comparaison porte sur ce qui était enregistré LA DERNIÈRE FOIS, et non
   * sur le contenu des champs : c'est ce qui permet de recharger après un
   * enregistrement sans écraser une frappe en cours.
   */
  const attendue = vueAttendue(`${type}/${langueActive}`, enregistre, defaut);
  const [vue, setVue] = useState(attendue);
  const [objet, setObjet] = useState(attendue.objet);
  const [corps, setCorps] = useState(attendue.corps);
  if (aResynchroniser(vue, attendue)) {
    setVue(attendue);
    setObjet(attendue.objet);
    setCorps(attendue.corps);
  }

  const variables = useMemo(() => description.variables, [description]);

  const enregistrer = (champ: 'objet' | 'corps', valeur: string) => {
    /*
     * Rien n'a bougé : on n'écrit pas.
     *
     * L'enregistrement part au `blur`, et un `blur` se produit aussi quand on
     * traverse un champ sans y toucher. Comme un champ vide veut dire « reviens
     * au texte d'origine », écrire sans regarder revenait à effacer le modèle
     * de quiconque cliquait dedans par erreur.
     */
    if (!aEnregistrer(valeur, attendue[champ])) return;
    onEnregistrer(
      { modelesMail: { [type]: { [langueActive]: { [champ]: valeur } } } },
      valeur.trim()
        ? `${description.libelle} — ${champ} enregistré.`
        : `${description.libelle} — ${champ} ramené au modèle d'origine.`,
    );
  };

  return (
    <div className="card">
      <h2>Modèles de mail</h2>
      <p className="hint">
        Tous les mails que l&apos;application compose. Ils sont <strong>relus avant
        envoi</strong> : ce que vous réglez ici est le texte qui s&apos;ouvre, pas forcément
        celui qui part. <strong>Exception</strong> : les mails d&apos;expédition et de suivi
        déclenchés par une découverte sur le portail Fortemix partent seuls, sans relecture —
        pour eux, ce texte est exactement celui que reçoit le client. Les champs portent le texte appliqué aujourd&apos;hui ; le vider le ramène au
        modèle d&apos;origine.
      </p>

      <div className="field">
        <label htmlFor="mm-type">Mail</label>
        <select
          id="mm-type" value={type}
          onChange={(e) => setType(e.target.value as TypeMail)}
        >
          {TYPES_MAIL.map((t) => (
            <option key={t} value={t}>{MODELES_MAIL[t].libelle}</option>
          ))}
        </select>
        <p className="indice">{description.aQuoiCaSert}</p>
      </div>

      {description.langues.length > 1 && (
        <div className="field">
          <label htmlFor="mm-langue">Langue</label>
          <select id="mm-langue" value={langueActive} onChange={(e) => setLangue(e.target.value)}>
            {description.langues.map((l) => (
              <option key={l} value={l}>
                {LIBELLE_LANGUE[l as 'fr' | 'en'] ?? l.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label htmlFor="mm-objet">Objet</label>
        <input
          id="mm-objet" value={objet} maxLength={300}
          placeholder={defaut.objet}
          onChange={(e) => setObjet(e.target.value)}
          onBlur={() => enregistrer('objet', objet)}
        />
      </div>

      <div className="field">
        <label htmlFor="mm-corps">Corps</label>
        <textarea
          id="mm-corps" rows={14} value={corps} maxLength={8000}
          placeholder={defaut.corps}
          onChange={(e) => setCorps(e.target.value)}
          onBlur={() => enregistrer('corps', corps)}
        />
      </div>

      <p className="hint">
        Variables remplies depuis la commande :{' '}
        {variables.map((v, i) => (
          <span key={v.nom}>
            {i > 0 && ', '}
            <span className="mono">{v.nom}</span> ({v.description})
          </span>
        ))}
        .
      </p>
      <p className="hint" style={{ marginBottom: 0 }}>
        La signature est ajoutée par la boîte <span className="mono">contact@</span> à
        l&apos;envoi : ne la recopiez pas ici, elle apparaîtrait en double.
      </p>
    </div>
  );
}
